/**
 * Everything that is a SIDE EFFECT of a store transition, in ONE subscription.
 *
 * Keeping these out of the reducers is what makes the reducers testable and
 * what makes a duplicate `game_end_response` harmless: each effect is keyed
 * idempotently on the result key / gameUuid.
 *
 * M0 covers: the end-of-game modal + mandatory `end_game_confirmed`, the three
 * localStorage mirrors PlayChess writes, and the move-ack watchdog. M1b adds the
 * game-START write of `lastGameTimeControl` (the lobby reads it back) — sounds
 * live in `rail/useSounds`. Fix A adds the snap back to live when it becomes my
 * turn (D3) and the mount-time SEEDING of every edge (P1-11). Fix V3 adds the
 * thinking-time gate's EXPIRY, turned into a store transition
 * (`actions.clearThinkingGate`) instead of a selector's own `Date.now()` —
 * see `onThinkingGate` below for why this one DOES evaluate at mount instead
 * of only seeding. Fix Z3 makes BOTH `lastGameTimeControl` writes skip an
 * assisted (Play & Learn) game: the lobby's memory is the control the player
 * CHOSE, and Play & Learn's fixed 60+15 is the mode's, not a preference.
 * TODO M2: the league/club POSTs (L2890-2949).
 */
import {useEffect} from 'react';

import {setItem as setLocalStorage} from '@/Store/liveGame/platform/storage';
import {bridgeSetShowEndGameModal} from '@/Store/liveGame/gamePlayStoreWrites';
import {id} from '@/Store/liveGame/state';
import {liveGameStore} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';

import {useV2Ui} from '../theme/useV2Ui';

/** protocol contract client rule 1: resync if a move is never acknowledged. */
const MOVE_ACK_TIMEOUT_MS = 3000;

export function useGameplayEffects(): void {
  useEffect(() => {
    /** result keys whose modal has already been shown (never re-delay one). */
    let shownResultKey: string | null = null;
    let modalTimer: any = null;
    let lastGameUuid: string | null = null;
    /** the table whose start already wrote `lastGameTimeControl` */
    let lastStartedTableId: number | null = null;
    let lastMovesRef: string[] | null = null;
    let lastOpponentKey: string | null = null;
    let watchdogFired = false;
    /** was it my turn on the previous state? (the D3 snap is an EDGE) */
    let wasMyTurn = false;
    /** fix V3: the pending expiry timer for the CURRENT `thinkingGateUntil` */
    let gateTimer: any = null;
    /** `${thinkingGateUntil}|${phase}` as last SEEN, so an unrelated store
     * write (a clock sync, a chat tick) is a no-op instead of re-arming */
    let lastGateKey: string | null = null;

    const confirmEnd = () => {
      liveGameStore.getState().actions.endGameConfirmed();
    };

    const onEnd = (state: LiveGameState) => {
      const result = state.game.result;
      if (!result || !state.game.endAckNeeded) {
        return;
      }
      if (shownResultKey === result.key) {
        // A re-sent end (the server repeats it until we confirm): ack again,
        // but do NOT re-open or re-delay the modal.
        confirmEnd();
        return;
      }
      shownResultKey = result.key;
      // PlayChess order (L3050-3057 then L3966-3981): modal first, confirm
      // after — checkmate/stalemate hold the modal back 1.5 s.
      const show = () => {
        bridgeSetShowEndGameModal(true);
        confirmEnd();
      };
      if (result.modalDelayMs > 0) {
        modalTimer = setTimeout(show, result.modalDelayMs);
      } else {
        show();
      }
    };

    const onStorage = (state: LiveGameState) => {
      const result = state.game.result;
      // PlayChess L626-643: written on every new game_uuid, i.e. at the END of a
      // game (`setGameUuid` only fires in game_end_response, L2980/2995/3012).
      // fix Z3: NEVER for an assisted (Play & Learn) game — its control is the
      // mode's own fixed 60+15, not a preference, and writing it here is what
      // left the Play tab on 60+15 with nothing selected (owner, 2026-10-06).
      if (result?.gameUuid && result.gameUuid !== lastGameUuid) {
        lastGameUuid = result.gameUuid;
        if (!state.game.assisted) {
          setLocalStorage('lastGameTimeControl', {
            moveTimer: state.game.tcMs,
            Increment: state.game.incMs,
          });
        }
      }
      // …and ALSO on game start (M1b): the key is the lobby's cross-session
      // memory (read at L539-553, mirrored by QuickPlay's model), so a game that
      // never reaches an end — a mid-game reload — must not leave it stale.
      // fix Z3: same assisted guard as the end-of-game write above.
      if (
        state.game.tableId !== null &&
        state.game.tableId !== lastStartedTableId &&
        state.game.tcMs > 0 &&
        (state.game.phase === 'starting' || state.game.phase === 'playing')
      ) {
        lastStartedTableId = state.game.tableId;
        if (!state.game.assisted) {
          setLocalStorage('lastGameTimeControl', {
            moveTimer: state.game.tcMs,
            Increment: state.game.incMs,
          });
        }
      }
      // PlayChess L573-578 reads this back on mount to seed the notation.
      if (state.game.moves !== lastMovesRef) {
        lastMovesRef = state.game.moves;
        if (state.game.moves.length > 0) {
          setLocalStorage(
            'moves',
            state.game.moves.map(move => ({
              move,
              matched: false,
              isHumanMove: false,
            })),
          );
        }
      }
      // PlayChess L646-670 (written, never read back today — kept for parity).
      const opponentKey = `${state.game.opponentName}|${state.game.gameDetails}|${state.game.assisted}`;
      if (state.game.opponentName && opponentKey !== lastOpponentKey) {
        lastOpponentKey = opponentKey;
        setLocalStorage('opponent', {
          displayName: state.game.opponentName,
          rating: state.game.opponentRating,
          startfen: state.game.startFen,
          assistedGameplay: state.game.assisted,
          gameDetails: state.game.gameDetails,
        });
      }
    };

    const onMoveAck = (state: LiveGameState) => {
      const sentAt = state.meta.pendingMoveSentAt;
      if (!sentAt) {
        watchdogFired = false;
        return;
      }
      // Only a LIVE game can be out of sync (P0-1). A stamp that outlives the
      // game — the game end arriving instead of the move ack — must never
      // reach `fetch_game_state`: the reply is `no_ongoing_game`.
      if (state.game.phase !== 'playing') {
        return;
      }
      if (watchdogFired || Date.now() - sentAt < MOVE_ACK_TIMEOUT_MS) {
        return;
      }
      // Out-of-turn/late moves are dropped SILENTLY by the server, so a
      // missing ack is the only signal that we are out of sync.
      watchdogFired = true;
      console.info('[V2] move ack timeout');
      liveGameStore.getState().actions.fetchGameState();
      liveGameStore.setState(s => ({
        ...s,
        meta: {...s.meta, pendingMoveSentAt: null},
      }));
    };

    /**
     * Decision D3: browsing back SNAPS to live the moment it becomes my turn.
     *
     * Reading an old ply while the opponent thinks is the point of the notation;
     * being stuck on it when my clock starts is not — the board is `disabled`
     * while browsing, so a user who forgot they were browsing would watch their
     * own time run out on a position that is not the game. The existing click on
     * the board (`stage/BoardFrame`'s snap catcher) stays.
     *
     * An EDGE, not a condition: it fires on the transition into my turn, so a
     * user who then browses back ON PURPOSE during their own move is left
     * alone. `wasMyTurn` is seeded at mount (see `seedEdges`), so a remount
     * mid-turn does not yank the board either.
     */
    const onMyTurn = (state: LiveGameState) => {
      const myTurn =
        state.game.phase === 'playing' &&
        !!state.session.me &&
        !!state.game.turnUserId &&
        id(state.game.turnUserId) === state.session.me;
      if (myTurn && !wasMyTurn && useV2Ui.getState().viewPly !== null) {
        useV2Ui.getState().setViewPly(null);
      }
      wasMyTurn = myTurn;
    };

    const clearGateTimer = () => {
      if (gateTimer) {
        clearTimeout(gateTimer);
        gateTimer = null;
      }
    };

    /**
     * Fix V3: the OTHER half of the touch-move-during-the-gate bug. Nothing
     * before this turned `thinkingGateUntil`'s EXPIRY into a store
     * transition — it was only ever WRITTEN (armed/cleared by
     * `reducers/game.ts`), so `isThinkingGated` could sit stale-true between
     * unrelated writes: the EventDock's own 250 ms tick hid "Thinking time!"
     * on time while the board — and any piece `touchPiece` had already
     * restricted through the open gate — stayed disabled. This schedules the
     * LOCAL `clearThinkingGate` action for `until + 25ms`, the old lane's own
     * `setTimeout` (`PlayChess/index.js` L2688-2691) made into a transition
     * every selector re-evaluates from together.
     *
     * Keyed on `until`+`phase` (not applied unconditionally like `onEnd` etc.)
     * so a re-arm or a phase change always cancels the OLD timer first —
     * "cancel on change/unmount/phase change" — while an unrelated tick with
     * the same pair is a no-op. Deliberately evaluated at MOUNT too (unlike
     * `seedEdges`'s silent trackers): a gate already past when this screen
     * (re)mounts must clear at once, not wait for a `until` that will never
     * change again on its own.
     */
    const onThinkingGate = (state: LiveGameState) => {
      const until = state.game.thinkingGateUntil;
      const {phase} = state.game;
      const key = `${until}|${phase}`;
      if (key === lastGateKey) {
        return;
      }
      lastGateKey = key;
      clearGateTimer();
      if (until === null || phase !== 'playing') {
        return;
      }
      // the RAW deadline, same comparison `clearThinkingGate` itself makes —
      // the +25ms below is a SCHEDULING margin for the timer, not part of
      // deciding whether the gate is already due.
      if (until <= Date.now()) {
        liveGameStore.getState().actions.clearThinkingGate();
        return;
      }
      gateTimer = setTimeout(() => {
        liveGameStore.getState().actions.clearThinkingGate();
      }, until - Date.now() + 25);
    };

    const apply = (state: LiveGameState) => {
      onEnd(state);
      onStorage(state);
      onMoveAck(state);
      onMyTurn(state);
      onThinkingGate(state);
    };

    /**
     * Mount-time SEEDING, never application (P1-11; the app lane fixed this
     * first and this is its `seedEdges`).
     *
     * The hook used to `apply(getState())` on mount, which REPLAYED whatever
     * was in the store. The store survives a remount of the screen — a route
     * change, a layout flip between the phone and desktop trees — so a game
     * that had just ended was still sitting there with `endAckNeeded` set, and
     * re-entering Play re-opened the result modal through the bridge, re-sent
     * `end_game_confirmed` and re-wrote the three localStorage mirrors for a
     * game that is over. Every edge tracker is now seeded from the current
     * state WITHOUT side effects — the rule `rail/useSounds`'s own seed follows
     * — and only transitions from here on fire.
     *
     * The move-ack watchdog is deliberately NOT seeded shut: a
     * `pendingMoveSentAt` that is already stale at mount is exactly the
     * out-of-sync case the watchdog exists for, and the 1 s interval below
     * picks it up on its own — one tick later, not inside the mount.
     */
    const seedEdges = (state: LiveGameState) => {
      const result = state.game.result;
      shownResultKey = result?.key ?? null;
      lastGameUuid = result?.gameUuid ?? null;
      lastStartedTableId = state.game.tableId;
      lastMovesRef = state.game.moves;
      lastOpponentKey = state.game.opponentName
        ? `${state.game.opponentName}|${state.game.gameDetails}|${state.game.assisted}`
        : null;
      wasMyTurn =
        state.game.phase === 'playing' &&
        !!state.session.me &&
        !!state.game.turnUserId &&
        id(state.game.turnUserId) === state.session.me;
    };

    seedEdges(liveGameStore.getState());
    // V3: NOT part of `seedEdges` — a gate already past at mount must clear
    // at once (see `onThinkingGate`'s own doc), where every other tracker
    // above is seeded silently on purpose (P1-11).
    onThinkingGate(liveGameStore.getState());

    const unsubscribe = liveGameStore.subscribe(apply);
    // The watchdog must also fire while nothing else changes.
    const interval = setInterval(
      () => onMoveAck(liveGameStore.getState()),
      1000,
    );

    return () => {
      unsubscribe();
      clearInterval(interval);
      clearGateTimer();
      if (modalTimer) {
        clearTimeout(modalTimer);
      }
    };
  }, []);
}

export default useGameplayEffects;
